import classNames from 'classnames';
import Link from 'next/link';
import { ButtonHTMLAttributes, ReactNode } from 'react';

export interface DefaultButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string;
  children?: ReactNode;
  /** Background color class. Default: 'bg-base-100' */
  bgColor?: string;
  /** Border color class. Default: 'border border-gray-200 dark:border-gray-800' */
  borderColor?: string;
  /** Text color class. Default: 'text-base-content' */
  textColor?: string;
  /** Hover background color class. Default: 'hover:bg-base-300' */
  hoverBgColor?: string;
  /** Hover text color class. Default: '' (no change) */
  hoverTextColor?: string;
}

const DefaultButton = ({
  href,
  onClick,
  className,
  children,
  type = 'button',
  disabled,
  bgColor = 'bg-base-100',
  borderColor = 'border border-gray-200 dark:border-gray-800',
  textColor = 'text-base-content',
  hoverBgColor = 'hover:bg-base-300',
  hoverTextColor = 'dark:hover:text-white',
  ...props
}: DefaultButtonProps) => {
  const baseClasses = classNames(
    'btn',
    bgColor,
    borderColor,
    textColor,
    hoverBgColor,
    hoverTextColor,
    className
  );

  if (href && !disabled) {
    // Exclude button-specific props that don't apply to Link
    const { type: _type, ...linkProps } = props as Record<string, unknown>;
    return (
      <Link
        href={href}
        className={baseClasses}
        {...(linkProps as Omit<React.ComponentProps<typeof Link>, 'href' | 'className'>)}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={baseClasses}
      onClick={onClick}
      disabled={disabled}
      {...props}>
      {children}
    </button>
  );
};

export default DefaultButton;
